import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, Container, Drawer, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { Block, CheckCircleOutline, Edit, PictureAsPdf } from '@material-ui/icons';
import { toast } from 'react-toastify';
import { dispatch, useSelector } from '../../../../redux/store';
import { PATH_DASHBOARD } from '../../../../routes/paths';
import useSettings from '../../../../hooks/useSettings';
import Page from '../../../../components/Page';
import HeaderBreadcrumbs from '../../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../../components/table/DataGridTable';
import PdfViewerDrawer from '../../../../components/pdfViewerDrawer';
import Iconify from '../../../../components/Iconify';
import UtilityBillPaymentDrawer from './utilityBillPayment/utilityBillPaymentDrawer';
import { getCashFlowSubCategories } from '../../../../redux/slices/financeRedux/cashFlowRedux';
import {
  getUtilityBillPaymentList,
  getUtilityBillsToBePaid,
  handleUtilityBillPaymentApproval,
} from '../../../../redux/slices/financeRedux/utilityBillPaymentRedux';
import { BANK_PAYMENT_TYPES, CASHFLOW_PAYMENT_STATUS, GLOBAL_LOCATION_ID } from '../../../../utils/constants';
import { BASE_URL, COMPANY_DATA_FOR_PDF } from '../../../../config';
import { accessVerify, capitalize, handleNumber, safeJSONParse, utcMoment } from '../../../../utils/common';
import { fCurrency } from '../../../../utils/formatNumber';

const dataModel = {
  id: null,
  dateTime: null,
  paymentDate: utcMoment().format('YYYY-MM-DD'),
  scheduledDate: utcMoment().format('YYYY-MM-DD'),
  paidDate: null,
  utilityBillId: null,
  cashbookLedgerId: null,
  bankPaymentType: null,
  referenceNote: '',
  amount: '',
  locationId: null,
  status: 2,
  addedBy: null,
  approvedBy: null,
  paymentMarkedBy: null,
  linkedDocuments: [],
};

const getDataModel = () => ({
  ...dataModel,
  paymentDate: utcMoment().format('YYYY-MM-DD'),
  scheduledDate: utcMoment().format('YYYY-MM-DD'),
  linkedDocuments: [],
});

const BILL_TYPES = [
  { label: 'Regular Bill', value: 'REGULAR' },
  { label: 'Service Bill', value: 'SERVICE' },
];

const parseJsonArray = (value) => {
  const parsed = safeJSONParse(value);
  return Array.isArray(parsed) ? parsed : [];
};

const normalizeDocuments = (linkedDocuments) =>
  parseJsonArray(linkedDocuments)
    .map((value) => {
      if (typeof value === 'string') return value;
      return value?.url;
    })
    .filter((value) => value);

const getDateForDisplay = (value) => (value ? utcMoment(value).format('YYYY-MM-DD') : '-');

const getBillDescription = (bill) => {
  const period =
    bill?.billingPeriodStart || bill?.billingPeriodEnd ? `Period: ${getDateForDisplay(bill?.billingPeriodStart)} to ${getDateForDisplay(bill?.billingPeriodEnd)}` : null;
  const readings = bill?.billType === 'REGULAR' && bill?.accountBillingType === 'METERED' ? `Readings: ${bill?.previousReading ?? '-'} to ${bill?.currentReading ?? '-'}` : null;
  const service = bill?.billType === 'SERVICE' ? `Service: ${bill?.serviceDescription || '-'}` : null;

  return [BILL_TYPES.find((value) => value.value === bill?.billType)?.label || bill?.billType, period, readings, service].filter(Boolean).join(' | ');
};

const parsePaymentWorkflowLogs = (rowData) =>
  parseJsonArray(rowData?.utility_payment_workflow_logs).map((log) => ({
    ...log,
    date: log?.date ? utcMoment(log.date).format('YYYY-MM-DD HH:mm:ss') : '',
  }));

const parseUtilityBillWorkflowLogs = (rowData) =>
  parseJsonArray(rowData?.utilityBillWorkflowLog).map((log) => ({
    ...log,
    date: log?.date ? utcMoment(log.date).format('YYYY-MM-DD HH:mm:ss') : '',
  }));

export default function UtilityBillPayments({ statusValue = 0, statusName = '', viewPermission = '' }) {
  const { themeStretch } = useSettings();
  const { selectedLocation } = useSelector((state) => state.location);
  const { utilityBillPaymentList, addData } = useSelector((state) => state.utilityBillPayment);

  const [rowSelection, setRowSelection] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [isAdd, setIsAdd] = useState(true);
  const [selectedDataObj, setSelectedDataObj] = useState(getDataModel);
  const [selectedRowForMarkPayment, setSelectedRowForMarkPayment] = useState(null);
  const [isMarkPaymentDrawerOpen, setIsMarkPaymentDrawerOpen] = useState(false);
  const [paymentDate, setPaymentDate] = useState('');
  const [isPdfDrawerOpen, setIsPdfDrawerOpen] = useState(false);
  const [pdfRow, setPdfRow] = useState(null);
  const [paymentVoucher, setPaymentVoucher] = useState({});

  useEffect(() => {
    if (selectedLocation?.id) {
      dispatch(getUtilityBillPaymentList(selectedLocation.id));
      dispatch(getUtilityBillsToBePaid(selectedLocation.id));
    }
  }, [selectedLocation?.id]);

  useEffect(() => {
    dispatch(getCashFlowSubCategories());
  }, []);

  useEffect(() => {
    if (addData?.time && selectedLocation?.id) {
      dispatch(getUtilityBillPaymentList(selectedLocation.id));
      dispatch(getUtilityBillsToBePaid(selectedLocation.id));
      setIsModelOpen(false);
      setSelectedDataObj(getDataModel());
    }
  }, [addData, selectedLocation?.id]);

  const filteredPaymentList = useMemo(
    () => (utilityBillPaymentList || []).filter((value) => Number(value?.status) === Number(statusValue)),
    [statusValue, utilityBillPaymentList]
  );

  const totalAmount = useMemo(() => filteredPaymentList.reduce((total, value) => total + handleNumber(value?.amount), 0), [filteredPaymentList]);

  const handleDownload = (row) => {
    const bill = row?.original || {};
    const totalAmount = handleNumber(bill?.totalAmount);
    const paidAmount = handleNumber(bill?.paidAmount);
    const utilityBillDocuments = normalizeDocuments(bill?.utilityBillLinkedDocuments);
    const paymentDocuments = normalizeDocuments(bill?.linkedDocuments);
    const utilitySupplierDetails = {
      name: bill?.utilitySupplierName || '-',
      address: bill?.utilitySupplierAddress || '',
      phone: bill?.utilitySupplierContactNumber || '',
    };
    const companyDetails = {
      name: COMPANY_DATA_FOR_PDF?.name,
      address: bill?.locationAddress || '',
      phone: bill?.locationContactNumber || '',
    };

    setPdfRow({
      sourceRow: bill,
      invoiceNumber: `UB-${bill?.utilityBillId}`,
      createDate: bill?.billDate ? utcMoment(bill.billDate).format('DD MMMM yyyy').toString() : '-',
      dueDate: bill?.dueDate ? utcMoment(bill.dueDate).format('DD MMMM yyyy').toString() : '-',
      status: bill?.utilityBillStatus || '-',
      discount: 0,
      taxes: 0,
      paidAmount,
      subTotalPrice: totalAmount,
      totalPrice: totalAmount - paidAmount,
      topic: 'Utility Bill',
      referenceNumber: bill?.referenceNumber,
      invoiceFrom: utilitySupplierDetails,
      invoiceTo: companyDetails,
      items: [
        {
          id: 1,
          title: `${bill?.utilityCategoryName || 'Utility'} - ${bill?.billingAccountName || bill?.utilityAccountName || '-'}`,
          description: getBillDescription(bill),
          quantity: 1,
          measurementLabel: 'Billing Type',
          measurementValue: bill?.accountBillingType === 'METERED' ? 'Metered' : bill?.accountBillingType === 'NON_METERED' ? 'Non Metered' : '-',
          price: totalAmount,
        },
      ],
      stockCodes: [],
      linkedDocuments: [...utilityBillDocuments, ...paymentDocuments].map((url) => url?.replace('https://storage.googleapis.com/bubblemania/', `${BASE_URL}imageViewer?path=`)),
    });

    setPaymentVoucher({
      voucherId: `UtilityVoucher_${bill?.id}`,
      voucherStatus: CASHFLOW_PAYMENT_STATUS?.find((value) => value.id === Number(bill?.status))?.name,
      voucherCreatedDate: bill?.dateTime ? utcMoment(bill.dateTime).format('DD MMMM yyyy').toString() : '-',
      voucherScheduledDate: bill?.scheduledDate ? utcMoment(bill.scheduledDate).format('DD MMMM yyyy').toString() : '-',
      voucherPaymentMethod: `${bill?.cashBookLedger || '-'}${
        bill?.bankPaymentType ? ` - ${BANK_PAYMENT_TYPES?.find((value) => value.id === Number(bill.bankPaymentType))?.label || '-'}` : ''
      }`,
      voucherReferenceNumber: bill?.referenceNote || '-',
      voucherInitiatedByUser: bill?.addedUserName || '-',
      voucherApprovedByUser: bill?.approvedByName || '-',
      voucherMarkedBy: bill?.paymentMarkedByName || '-',
      paymentFrom: companyDetails,
      paymentTo: utilitySupplierDetails,
      workflowLogs: parsePaymentWorkflowLogs(bill),
    });

    setIsPdfDrawerOpen(true);
  };

  const handleSubmitPayment = () => {
    if (!paymentDate) {
      toast.error('Please enter a payment date.');
      return;
    }
    dispatch(handleUtilityBillPaymentApproval({ id: selectedRowForMarkPayment?.original?.id, status: 5, paidDate: paymentDate }));
    setIsMarkPaymentDrawerOpen(false);
    setSelectedRowForMarkPayment(null);
    setPaymentDate('');
  };

  const columns = [
    { accessorKey: 'id', header: 'Voucher ID' },
    {
      accessorKey: 'utilityBillId',
      header: 'Utility Bill',
      Cell: ({ row }) => `UB-${row?.original?.utilityBillId}`,
    },
    { accessorKey: 'locationName', header: 'Location' },
    {
      accessorKey: 'billingAccountName',
      header: 'Account / Meter',
      Cell: ({ row }) => `${row?.original?.billingAccountName || row?.original?.utilityAccountName || '-'}${row?.original?.meterNumber ? ` (${row?.original?.meterNumber})` : ''}`,
    },
    {
      accessorKey: 'utilitySupplierName',
      header: 'Supplier',
      Cell: ({ row }) => (row?.original?.utilitySupplierName ? `${row?.original?.utilitySupplierName}${row?.original?.utilitySupplierCode ? ` (${row?.original?.utilitySupplierCode})` : ''}` : '-'),
    },
    { accessorKey: 'referenceNumber', header: 'Bill Reference' },
    {
      accessorKey: 'dueDate',
      header: 'Due Date',
      Cell: ({ row }) => getDateForDisplay(row?.original?.dueDate),
    },
    {
      accessorKey: 'paymentDate',
      header: 'Payment Date',
      Cell: ({ row }) => getDateForDisplay(row?.original?.paymentDate),
    },
    {
      accessorKey: 'scheduledDate',
      header: 'Scheduled Date',
      Cell: ({ row }) => getDateForDisplay(row?.original?.scheduledDate),
    },
    {
      accessorKey: 'paidDate',
      header: 'Paid Date',
      Cell: ({ row }) => getDateForDisplay(row?.original?.paidDate),
    },
    {
      accessorKey: 'cashBookLedger',
      header: 'Cashbook Ledger',
      Cell: ({ row }) => row?.original?.cashBookLedger || '-',
    },
    {
      accessorKey: 'bankPaymentType',
      header: 'Payment Type',
      Cell: ({ row }) => BANK_PAYMENT_TYPES?.find((value) => value.id === Number(row?.original?.bankPaymentType))?.label || '-',
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      Cell: ({ row }) => fCurrency(row?.original?.amount),
    },
    {
      accessorKey: 'status',
      header: capitalize('status'),
      Cell: ({ row }) => CASHFLOW_PAYMENT_STATUS?.find((value) => value.id === Number(row?.original?.status))?.name || '-',
    },
    { accessorKey: 'addedUserName', header: 'Initiated By' },
    { accessorKey: 'approvedByName', header: 'Approved By' },
    { accessorKey: 'paymentMarkedByName', header: 'Payment Marked By' },
  ];

  return (
    <Page title={`Utility Bill Payment (${statusName})`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={`Utility Bill Payment (${statusName})`}
          links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Finance', href: PATH_DASHBOARD.finance.root }, { name: `Utility Bill Payment (${statusName})` }]}
          action={
            statusValue === 2 && accessVerify('FINANCE_UTILITY_BILL_INITIATE_ADD') ? (
              <Button
                variant='contained'
                startIcon={<Iconify icon='eva:plus-fill' />}
                onClick={() => {
                  if (Number(selectedLocation?.id) === GLOBAL_LOCATION_ID) {
                    toast.error('Sorry, you cannot initiate a utility bill payment while the Global location is selected. Please select the outlet for the bill and try again.');
                    return;
                  }
                  setIsModelOpen(true);
                  setIsAdd(true);
                  setSelectedDataObj(getDataModel());
                }}
              >
                Initiate Utility Bill Payment
              </Button>
            ) : null
          }
        />

        {accessVerify(`FINANCE_UTILITY_BILL_${viewPermission}_VIEW`) && (
          <>
            <Card sx={{ m: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', p: 2 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                  <Typography variant='body2' sx={{ color: 'text.secondary', fontWeight: '400' }}>
                    Total Amount
                  </Typography>
                  <Typography variant='h6' sx={{ color: 'red', fontWeight: '500' }}>
                    {fCurrency(totalAmount)}
                  </Typography>
                </Box>
              </Box>
            </Card>
            <DataGridTable
              name='UtilityBillPayment'
              data={filteredPaymentList}
              column={columns}
              isLoading={false}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              enableRowActions={true}
              renderRowActionItems={(row) => [
                <>
                  {Number(row?.original?.status) === 2 && accessVerify('FINANCE_UTILITY_BILL_INITIATE_UPDATE') && (
                    <>
                      <Tooltip title='Confirm Payment'>
                        <IconButton color='success' size='small' onClick={() => dispatch(handleUtilityBillPaymentApproval({ id: row?.original?.id, status: 3 }))}>
                          <CheckCircleOutline />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title='Decline Payment'>
                        <IconButton color='error' size='small' onClick={() => dispatch(handleUtilityBillPaymentApproval({ id: row?.original?.id, status: 4 }))}>
                          <Block />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {Number(row?.original?.status) === 3 && accessVerify('FINANCE_UTILITY_BILL_MARK_UPDATE') && (
                    <Tooltip title='Mark Payment'>
                      <Button
                        color='success'
                        onClick={() => {
                          setSelectedRowForMarkPayment(row);
                          setPaymentDate(utcMoment().format('YYYY-MM-DD'));
                          setIsMarkPaymentDrawerOpen(true);
                        }}
                        size='small'
                        startIcon={<CheckCircleOutline fontSize='small' />}
                        sx={{ padding: '4px 8px', minWidth: 0, textTransform: 'none', fontSize: '0.875rem' }}
                      >
                        Paid
                      </Button>
                    </Tooltip>
                  )}
                  {[2, 3].includes(Number(row?.original?.status)) && accessVerify('FINANCE_UTILITY_BILL_INITIATE_UPDATE') && (
                    <Tooltip title='Edit Documents'>
                      <IconButton
                        color='warning'
                        onClick={() => {
                          setIsAdd(false);
                          setSelectedDataObj({
                            ...row?.original,
                            linkedDocuments: normalizeDocuments(row?.original?.linkedDocuments),
                          });
                          setIsModelOpen(true);
                        }}
                      >
                        <Edit />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title='Download PDF'>
                    <IconButton color='error' onClick={() => handleDownload(row)}>
                      <PictureAsPdf />
                    </IconButton>
                  </Tooltip>
                </>,
              ]}
            />
          </>
        )}

        <Drawer anchor='right' open={isMarkPaymentDrawerOpen} onClose={() => setIsMarkPaymentDrawerOpen(false)}>
          <Box sx={{ width: 320, p: 3 }}>
            <Typography variant='h6' sx={{ mb: 2 }}>
              Enter Payment Date
            </Typography>
            <Typography variant='body2' sx={{ mb: 1 }}>
              Utility Bill: UB-{selectedRowForMarkPayment?.original?.utilityBillId || '-'}
            </Typography>
            <Typography variant='body2' sx={{ mb: 1 }}>
              Due Date: {getDateForDisplay(selectedRowForMarkPayment?.original?.dueDate)}
            </Typography>
            <TextField
              label='Payment Date'
              type='date'
              fullWidth
              value={paymentDate}
              onChange={(event) => setPaymentDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              margin='normal'
            />
            <Button variant='contained' color='primary' onClick={handleSubmitPayment} fullWidth>
              Submit Payment
            </Button>
          </Box>
        </Drawer>

        <PdfViewerDrawer open={isPdfDrawerOpen} onClose={() => setIsPdfDrawerOpen(false)} paymentVoucher={paymentVoucher} invoice={pdfRow} workflowLog={parseUtilityBillWorkflowLogs(pdfRow?.sourceRow)} />
        <UtilityBillPaymentDrawer
          isModelOpen={isModelOpen}
          setIsModelOpen={setIsModelOpen}
          selectedDataObj={selectedDataObj}
          setSelectedDataObj={setSelectedDataObj}
          isAdd={isAdd}
        />
      </Container>
    </Page>
  );
}
